import React, { FC, useEffect, useMemo, useState } from 'react';

import { ApolloQueryResult, gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FilterSelect, MaybeTooltip } from '@modernloop/shared/components';
import { PublicError } from '@modernloop/shared/helper-components';
import { CircularProgress, ListItem, ListItemButton, Stack, Typography } from '@mui/material';
import { isNil, merge, uniqBy } from 'lodash';
import { useDebounce, useDebouncedCallback } from 'use-debounce';

import {
  InterviewModuleFragment as InterviewModuleFragmentGQL,
  InterviewModuleSearchQuery,
  InterviewModulesOrderByProperty,
  Sort,
  useInterviewModuleSearchLazyQuery,
} from 'src/generated/mloop-graphql';

import { DEBOUNCE_TIMEOUT, PAGER_DEFAULT } from 'src/constants';

export const InterviewModuleFragment = gql`
  fragment InterviewModule on InterviewModule {
    id
    name
    shadowsRequired
    reverseShadowsRequired
    weeklyNumInterviewLimit
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InterviewModulesSearchQuery = gql`
  ${InterviewModuleFragment}
  query InterviewModuleSearch($input: InterviewModuleSearchInput!) {
    interviewModuleSearch(input: $input) {
      total
      items {
        ...InterviewModule
      }
    }
  }
`;

const EMPTY_ROW_ID = 'loading-empty-row';
const PAGE_LIMIT = PAGER_DEFAULT;

interface Props {
  interviewModule?: InterviewModuleFragmentGQL;
  onInterviewModuleChange: (interviewModule: InterviewModuleFragmentGQL) => void;
  filterOptions?: (options: InterviewModuleFragmentGQL[]) => InterviewModuleFragmentGQL[];
}

const InterviewModulesSelect: FC<Props> = ({ interviewModule, onInterviewModuleChange, filterOptions }) => {
  const { t } = useTranslation();
  const [fetchingMore, setFetchingMore] = useState(false);
  const [search, setSearch] = useState('');

  const [debouncedSearch] = useDebounce(search, DEBOUNCE_TIMEOUT);

  const [
    fetchInterviewModules,
    {
      data: interviewModulesData,
      loading: loadingInterviewerModules,
      error: interviewModulesError,
      updateQuery,
      fetchMore,
    },
  ] = useInterviewModuleSearchLazyQuery();

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    fetchInterviewModules({
      variables: {
        input: {
          pageInput: {
            limit: PAGE_LIMIT,
            offset: 0,
          },
          orderBy: { property: InterviewModulesOrderByProperty.Name, direction: Sort.Asc },
          search: debouncedSearch,
        },
      },
    });
  }, [debouncedSearch, fetchInterviewModules]);

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: InterviewModuleFragmentGQL) => {
    return (
      <ListItem {...props}>
        <ListItemButton>
          {option.id === EMPTY_ROW_ID && (
            <Stack justifyContent="center">
              <CircularProgress size={20} />
            </Stack>
          )}
          {option.id !== EMPTY_ROW_ID && <MaybeTooltip label={option.name} title={option.name} />}
        </ListItemButton>
      </ListItem>
    );
  };

  const getOptionLabel = (option: InterviewModuleFragmentGQL) => {
    if (option.id === EMPTY_ROW_ID) {
      return '';
    }

    return option.name;
  };

  const defaultGetLabel = () => {
    const label = isNil(interviewModule) ? 'Select interview module' : getOptionLabel(interviewModule);

    return (
      <Typography noWrap color="text.secondary">
        {label}
      </Typography>
    );
  };

  const handleChange = (_event, options: InterviewModuleFragmentGQL[]) => {
    onInterviewModuleChange(options[0]);
  };

  const handleScrollToEnd = () => {
    if (
      !fetchMore ||
      !interviewModulesData ||
      loadingInterviewerModules ||
      !interviewModulesData.interviewModuleSearch ||
      !interviewModulesData.interviewModuleSearch.items ||
      interviewModulesData.interviewModuleSearch.items.length === interviewModulesData.interviewModuleSearch.total
    ) {
      return;
    }

    setFetchingMore(true);
    // eslint-disable-next-line promise/catch-or-return
    fetchMore({
      variables: {
        input: {
          orderBy: { property: InterviewModulesOrderByProperty.Name, direction: Sort.Asc },
          pageInput: { limit: PAGE_LIMIT, offset: interviewModulesData.interviewModuleSearch.items.length ?? 0 },
          search: '',
        },
      },
    })
      .then((result: ApolloQueryResult<InterviewModuleSearchQuery>) => {
        // eslint-disable-next-line promise/always-return
        if (!updateQuery) {
          return;
        }

        updateQuery((prevResult: InterviewModuleSearchQuery) => {
          const { interviewModuleSearch } = prevResult;
          const newResults = merge({}, prevResult, result.data);

          if (newResults.interviewModuleSearch?.items) {
            newResults.interviewModuleSearch.items = uniqBy(
              [...(interviewModuleSearch?.items || []), ...(result.data?.interviewModuleSearch?.items || [])],
              'id'
            );
          }

          return { ...newResults };
        });
      })
      .finally(() => {
        setFetchingMore(false);
      });
  };

  const debouncedHandleScrollToEnd = useDebouncedCallback(handleScrollToEnd, DEBOUNCE_TIMEOUT);

  const options: InterviewModuleFragmentGQL[] = useMemo(() => {
    let result = (interviewModulesData?.interviewModuleSearch?.items || []) as InterviewModuleFragmentGQL[];

    if (fetchingMore) {
      result = [...result, { id: EMPTY_ROW_ID } as InterviewModuleFragmentGQL];
    }
    return result;
  }, [fetchingMore, interviewModulesData?.interviewModuleSearch?.items]);

  if (interviewModulesError) {
    return <PublicError error={t('hiring-dashboard-common:common.select.interviewModulesLoadError')} />;
  }

  return (
    <FilterSelect
      single
      options={options}
      loading={loadingInterviewerModules}
      placeholderText={t('hiring-dashboard-common:common.select.interviewModulePlaceholder')}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      onChange={handleChange}
      getLabel={defaultGetLabel}
      onScrollToEnd={debouncedHandleScrollToEnd}
      onInputChange={(_, inputValue) => {
        setSearch(inputValue);
      }}
      getChildSize={(child: React.ReactNode) => {
        if (!child) {
          return 0;
        }
        return 32;
      }}
      filterOptions={filterOptions}
    />
  );
};

export default InterviewModulesSelect;
